import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Sparkles, X } from "lucide-react";

const DISMISS_KEY = "pj_upgrade_dismissed";

const PERKS = ["50 proposals a month", "3 team seats", "Client e-signatures", "Priority AI drafting"];

/** Shown only while the workspace is on the free trial. */
export const UpgradeCard = ({ sub }) => {
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(sessionStorage.getItem(DISMISS_KEY) === "1");
  if (!sub || !sub.is_trial || hidden) return null;

  const left = Math.max(0, sub.proposals_left ?? 0);
  const expired = sub.status === "expired";

  return (
    <div
      data-testid="free-tier-upgrade-card"
      className="relative overflow-hidden rounded-2xl border border-indigo-500/25 bg-gradient-to-br from-indigo-500/12 via-panel to-panel p-5 sm:p-6"
    >
      <div className="absolute -top-20 -right-10 w-64 h-64 rounded-full bg-indigo-500/15 blur-3xl" />
      <button
        data-testid="dismiss-upgrade-card-btn"
        aria-label="Dismiss"
        onClick={() => {
          sessionStorage.setItem(DISMISS_KEY, "1");
          setHidden(true);
        }}
        className="absolute top-3 right-3 z-10 h-7 w-7 grid place-items-center rounded-lg text-zinc-500 hover:text-zinc-200 hover:bg-zinc-800/70 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="relative flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="flex-1">
          <p className="inline-flex items-center gap-1.5 text-[10px] font-num uppercase tracking-[0.2em] text-indigo-300">
            <Sparkles className="w-3 h-3" /> Free tier
          </p>
          <h3 className="mt-2 text-lg md:text-lg font-bold tracking-tight text-zinc-100">
            {expired
              ? "Your free trial has ended"
              : left === 0
                ? "You've used every free proposal"
                : `${left} free ${left === 1 ? "proposal" : "proposals"} left`}
          </h3>
          <p className="mt-1 text-sm text-zinc-400 max-w-xl">
            Upgrade to keep drafting, unlock team seats and remove the free-tier cap. Plans start at ₹799 a month —
            cancel anytime.
          </p>
          <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
            {PERKS.map((p) => (
              <li key={p} className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400">
                <Check className="w-3 h-3 text-emerald-400 shrink-0" />
                {p}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col sm:flex-row lg:flex-col gap-2 shrink-0">
          <button
            data-testid="dashboard-upgrade-btn"
            onClick={() => navigate("/checkout?plan=monthly")}
            className="h-11 px-5 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors"
          >
            Upgrade now <ArrowRight className="w-4 h-4" />
          </button>
          <button
            data-testid="dashboard-compare-plans-btn"
            onClick={() => navigate("/plans")}
            className="h-11 px-5 rounded-xl border border-zinc-800 hover:border-zinc-700 text-sm text-zinc-300 transition-colors"
          >
            Compare plans
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeCard;
